import React, { useState, useEffect } from "react";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../ui/select";
import { BaseFormFields, useBaseForm } from "./BaseFormFields";
import * as api from "../../lib/api";

const DEFECT_TYPES = [
    { id: "screen", label: "Écran (scintillement, pixels morts...)", icon: "bg-blue-400" },
    { id: "battery", label: "Batterie (autonomie dégradée)", icon: "bg-yellow-400" },
    { id: "keyboard_mouse", label: "Clavier / Souris (touche morte, dysfonctionnement)", icon: "bg-orange-400" },
    { id: "connectivity", label: "Connectique (ports USB, HDMI, réseau...)", icon: "bg-purple-400" },
    { id: "performance", label: "Performance (lenteur, surchauffe, bruit)", icon: "bg-red-400" },
    { id: "other", label: "Autre", icon: "bg-gray-400" },
];

const CRITICALITY_LEVELS = [
    { id: "blocking", label: "Bloquant total", icon: "bg-red-500", priority: "high", desc: "Impossible de travailler" },
    { id: "daily_nuisance", label: "Gêne quotidienne", icon: "bg-yellow-400", priority: "medium", desc: "Fonctionnel mais pénible" },
    { id: "intermittent", label: "Intermittent", icon: "bg-blue-400", priority: "low", desc: "Se produit de temps en temps" },
];

function OptionWithDot({ icon, label, desc }) {
    return (
        <span className="flex items-center gap-2.5">
            <span className={`w-2.5 h-2.5 rounded-full ${icon}`} />
            <span>{label}</span>
            {desc && <span className="text-[10px] text-gray-400 ml-auto">{desc}</span>}
        </span>
    );
}

export function DefectiveEquipmentForm({ onSubmit, initialData, readOnly, submitting, onTitleChange, allowedPriorities, ticketType, onTypeChange }) {
    const baseForm = useBaseForm("medium");
    const [equipment, setEquipment] = useState([]);
    const [selectedEquipment, setSelectedEquipment] = useState("");
    const [customEquipment, setCustomEquipment] = useState("");
    const [serialNumber, setSerialNumber] = useState("");
    const [defectType, setDefectType] = useState("screen");
    const [criticality, setCriticality] = useState("daily_nuisance");
    const [sinceWhen, setSinceWhen] = useState("");
    const [workaroundAvailable, setWorkaroundAvailable] = useState(false);
    const [workaroundDescription, setWorkaroundDescription] = useState("");

    useEffect(() => {
        api.getEquipmentCatalog().then(setEquipment).catch(console.error);
    }, []);

    // Auto-set priority from criticality
    const handleCriticalityChange = (val) => {
        setCriticality(val);
        const level = CRITICALITY_LEVELS.find((l) => l.id === val);
        if (level) baseForm.setPriority(level.priority);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const equipmentName = selectedEquipment === "__custom" ? customEquipment : (equipment.find((eq) => eq.id === selectedEquipment)?.name || selectedEquipment);
        onSubmit({
            title: baseForm.title || `Matériel défectueux — ${equipmentName}`,
            description: baseForm.description,
            priority: baseForm.priority,
            dynamic_fields: {
                equipment_id: selectedEquipment !== "__custom" ? selectedEquipment : null,
                equipment_name: equipmentName,
                serial_number: serialNumber || null,
                defect_type: defectType,
                criticality: criticality,
                since_when: sinceWhen || null,
                workaround_available: workaroundAvailable,
                workaround_description: workaroundAvailable ? workaroundDescription : null,
            },
        });
    };

    const currentDefect = DEFECT_TYPES.find((d) => d.id === defectType);
    const currentCriticality = CRITICALITY_LEVELS.find((c) => c.id === criticality);

    return (
        <form onSubmit={handleSubmit}>
            <BaseFormFields
                ticketType={ticketType}
                onTypeChange={onTypeChange}
                {...baseForm}
                onTitleChange={onTitleChange}
                    allowedPriorities={allowedPriorities}
                titlePlaceholder="Ex: Écran qui scintille sur le poste PC-2034"
                descriptionPlaceholder="Décrivez le comportement observé en détail..."
            >
                {/* Equipment selection */}
                <div className="form-group">
                    <Label className="form-label">Équipement concerné</Label>
                    <Select value={selectedEquipment} onValueChange={setSelectedEquipment}>
                        <SelectTrigger data-testid="equipment-select">
                            <SelectValue placeholder="Sélectionner un équipement" />
                        </SelectTrigger>
                        <SelectContent>
                            {equipment.map((eq) => (
                                <SelectItem key={eq.id} value={eq.id}>{eq.name} — {eq.type}</SelectItem>
                            ))}
                            <SelectItem value="__custom">Autre (saisie libre)</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                {selectedEquipment === "__custom" && (
                    <div className="form-group">
                        <Label className="form-label">Nom de l'équipement</Label>
                        <Input
                            value={customEquipment}
                            onChange={(e) => setCustomEquipment(e.target.value)}
                            placeholder="Ex: Imprimante couleur 3ème étage"
                            data-testid="custom-equipment"
                        />
                    </div>
                )}

                {/* Serial number */}
                <div className="form-group">
                    <Label className="form-label">Numéro de série / inventaire</Label>
                    <Input
                        value={serialNumber}
                        onChange={(e) => setSerialNumber(e.target.value)}
                        placeholder="Si connu"
                        data-testid="serial-number"
                    />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Defect type */}
                    <div className="form-group">
                        <Label className="form-label">Type de défaut</Label>
                        <Select value={defectType} onValueChange={setDefectType}>
                            <SelectTrigger data-testid="defect-type">
                                <SelectValue>
                                    {currentDefect && <OptionWithDot icon={currentDefect.icon} label={currentDefect.label} />}
                                </SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                                {DEFECT_TYPES.map((d) => (
                                    <SelectItem key={d.id} value={d.id}>
                                        <OptionWithDot icon={d.icon} label={d.label} />
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Criticality (auto-sets priority) */}
                    <div className="form-group">
                        <Label className="form-label">Criticité</Label>
                        <Select value={criticality} onValueChange={handleCriticalityChange}>
                            <SelectTrigger data-testid="criticality">
                                <SelectValue>
                                    {currentCriticality && <OptionWithDot icon={currentCriticality.icon} label={currentCriticality.label} />}
                                </SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                                {CRITICALITY_LEVELS.map((c) => (
                                    <SelectItem key={c.id} value={c.id}>
                                        <OptionWithDot icon={c.icon} label={c.label} desc={c.desc} />
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                {/* Since when */}
                <div className="form-group">
                    <Label className="form-label">Depuis quand</Label>
                    <Input
                        type="date"
                        value={sinceWhen}
                        onChange={(e) => setSinceWhen(e.target.value)}
                        data-testid="since-when"
                    />
                </div>

                {/* Workaround */}
                <div className="form-group">
                    <div className="flex items-center gap-2">
                        <Checkbox
                            id="workaround"
                            checked={workaroundAvailable}
                            onCheckedChange={setWorkaroundAvailable}
                            data-testid="workaround-checkbox"
                        />
                        <Label htmlFor="workaround" className="text-sm font-medium cursor-pointer">
                            Un contournement est disponible
                        </Label>
                    </div>
                </div>

                {workaroundAvailable && (
                    <div className="form-group">
                        <Label className="form-label">Description du contournement</Label>
                        <Textarea
                            value={workaroundDescription}
                            onChange={(e) => setWorkaroundDescription(e.target.value)}
                            placeholder="Ex: J'utilise un écran externe en attendant..."
                            data-testid="workaround-description"
                        />
                    </div>
                )}

                <p className="text-xs text-gray-400 mt-2">
                    Une photo du défaut est recommandée — utilisez la section "Pièces jointes" ci-dessous.
                </p>
            </BaseFormFields>

            <div className="mt-6 flex justify-end">
                <Button
                    type="submit"
                    disabled={!baseForm.title || submitting}
                    data-testid="submit-ticket"
                >
                    {submitting ? "Création..." : "Soumettre le ticket"}
                </Button>
            </div>
        </form>
    );
}
